export type ResolvedPlatform =
    | "youtube"
    | "spotify"
    | "soundcloud"
    | "applemusic"
    | "netflix"
    | "other";

const hasHostMatch = (hostname: string, targets: string[]): boolean => {
    const normalizedHost = String(hostname || "").toLowerCase();
    if (!normalizedHost) return false;

    return targets.some((target) => {
        const normalizedTarget = target.toLowerCase();
        return (
            normalizedHost === normalizedTarget ||
            normalizedHost.endsWith(`.${normalizedTarget}`)
        );
    });
};

export const inferPlatformFromUrl = (rawUrl: unknown): ResolvedPlatform => {
    const url = String(rawUrl || "").trim();
    if (!url) return "other";

    const lowered = url.toLowerCase();
    if (lowered.startsWith("spotify:")) return "spotify";
    if (lowered.startsWith("youtube:")) return "youtube";

    try {
        const parsed = new URL(url);
        const hostname = String(parsed.hostname || "").toLowerCase();

        if (hasHostMatch(hostname, ["youtube.com", "youtu.be"])) return "youtube";
        if (hasHostMatch(hostname, ["spotify.com"])) return "spotify";
        if (hasHostMatch(hostname, ["soundcloud.com"])) return "soundcloud";
        if (hasHostMatch(hostname, ["music.apple.com", "itunes.apple.com"])) return "applemusic";
        if (hasHostMatch(hostname, ["netflix.com"])) return "netflix";
    } catch {
        // Allow string-based fallback when URL parsing fails (e.g., malformed or partial URLs).
        if (/youtube\.com|youtu\.be/i.test(url)) return "youtube";
        if (/spotify\.com/i.test(url)) return "spotify";
        if (/soundcloud\.com/i.test(url)) return "soundcloud";
        if (/music\.apple\.com|itunes\.apple\.com/i.test(url)) return "applemusic";
        if (/netflix\.com/i.test(url)) return "netflix";
    }

    return "other";
};

export const normalizePlatformValue = (
    rawPlatform: unknown,
    rawUrl: unknown = ""
): ResolvedPlatform => {
    const platform = String(rawPlatform || "").trim().toLowerCase();
    const inferredFromUrl = inferPlatformFromUrl(rawUrl);

    if (!platform) return inferredFromUrl;

    if (platform.includes("youtube")) return "youtube";
    if (platform.includes("spotify")) return "spotify";
    if (platform.includes("soundcloud")) return "soundcloud";
    if (platform.includes("apple") || platform.includes("itunes")) return "applemusic";
    if (platform.includes("netflix")) return "netflix";

    if (
        platform.startsWith("http://") ||
        platform.startsWith("https://") ||
        platform.startsWith("spotify:") ||
        platform.startsWith("youtube:")
    ) {
        return inferPlatformFromUrl(platform);
    }

    return inferredFromUrl;
};
